import {
  SizingCycle,
  SizingCycleEvent,
  SizingDirection,
  SizingEventEvidence,
  SizingEventType,
  SizingModelDetail,
  SizingModelListItem,
  SizingModelResult,
  SizingModels,
  SizingOverview,
  SizingProblem,
  SizingStatus,
  SizingTrendPoint
} from '@/domain/sizing'
import {
  SizesuggestionspbGetSizingCycleResponse,
  SizesuggestionspbGetSizingModelResponse,
  SizesuggestionspbGetSizingOverviewResponse,
  SizesuggestionspbListSizingModelsResponse,
  SizesuggestionspbSizingCycleEventView,
  SizesuggestionspbSizingModelListItem,
  SizesuggestionspbSizingModelResult,
  SizesuggestionspbSizingTrendPoint
} from '@itsrever/rever-api'

// Rates travel as fractions and counts; every % the UI shows is computed here.

const MIN_UNITS_FOR_RATE = 5

function ratePct(returned?: number | null, units?: number | null): number | null {
  if (
    returned === undefined ||
    returned === null ||
    units === undefined ||
    units === null ||
    units < MIN_UNITS_FOR_RATE
  ) {
    return null
  }
  return (returned / units) * 100
}

function direction(
  retBefore: number | null | undefined,
  retAfter: number | null | undefined,
  unitsBefore: number | null | undefined,
  unitsAfter: number | null | undefined,
  // The counterfactual rate the euros were billed against. Deriving it from
  // retBefore instead rounds it onto whole returns, and on a short window that
  // rounding is larger than the effect the row is reporting.
  rateBefore?: number | null
): SizingDirection {
  const rateAfterPct = ratePct(retAfter, unitsAfter)
  const rateBeforePct =
    rateBefore != null && rateAfterPct !== null
      ? rateBefore * 100
      : ratePct(retBefore, unitsBefore)
  return {
    rateBeforePct,
    rateAfterPct,
    deltaPp:
      rateBeforePct !== null && rateAfterPct !== null
        ? rateAfterPct - rateBeforePct
        : null
  }
}

function toStatus(status?: string): SizingStatus {
  const known: SizingStatus[] = [
    'WORKING',
    'COLLECTING_DATA',
    'NO_CLEAR_EFFECT',
    'REDIAGNOSIS',
    'RETIRED'
  ]
  return known.find((s) => s === status) ?? 'COLLECTING_DATA'
}

function toProblem(problem?: string): SizingProblem {
  return problem === 'TOO_SMALL' ? 'TOO_SMALL' : 'TOO_BIG'
}

function toTrendPoint(
  point: SizesuggestionspbSizingTrendPoint
): SizingTrendPoint {
  const returned = (point.ret_small ?? 0) + (point.ret_big ?? 0)
  // int64 fields arrive as strings from the generated client.
  const unitsSold = Number(point.units_sold ?? 0)
  return {
    month: point.month ?? '',
    unitsSold,
    ratePct: ratePct(returned, unitsSold)
  }
}

export function toSizingOverview(
  data: SizesuggestionspbGetSizingOverviewResponse
): SizingOverview {
  return {
    cycleDate: data.cycle_date ?? '',
    savedEurToDate: data.saved_eur_to_date ?? 0,
    savedEurLast3m: data.saved_eur_last_3m ?? 0,
    modelsWithBanner: data.models_with_banner ?? 0,
    productsWithBanner: data.products_with_banner ?? 0,
    newModelsThisCycle: data.new_models_this_cycle ?? 0,
    avoidedReturnsMonth: data.avoided_returns_month ?? 0,
    avoidedEurMonth: data.avoided_eur_month ?? 0,
    statusCounts: {
      working: data.status_counts?.working ?? 0,
      collectingData: data.status_counts?.collecting_data ?? 0,
      noClearEffect: data.status_counts?.no_clear_effect ?? 0,
      rediagnosis: data.status_counts?.rediagnosis ?? 0,
      retired: data.status_counts?.retired ?? 0
    },
    trend: (data.trend ?? []).map(toTrendPoint)
  }
}

function toModelListItem(
  item: SizesuggestionspbSizingModelListItem
): SizingModelListItem {
  let rateAfterPct = ratePct(item.ret_total_after, item.units_after)
  // Same rounding trap as in direction(): only rate_total_before reconciles
  // with the euros. The counts stay as the fallback for rows persisted before
  // the rate was carried.
  let rateBeforePct =
    item.rate_total_before != null && rateAfterPct !== null
      ? item.rate_total_before * 100
      : ratePct(item.ret_total_before, item.units_before)
  // A frozen retirement keeps ratio and lift but not the underlying counts;
  // its own rates are recoverable from the pair: before = lift/(ratio-1).
  if (
    rateBeforePct === null &&
    item.ratio != null &&
    item.lift_pp != null &&
    item.ratio !== 1
  ) {
    const before = item.lift_pp / (item.ratio - 1)
    if (before > 0 && before <= 1) {
      rateBeforePct = before * 100
      rateAfterPct = before * item.ratio * 100
    }
  }
  return {
    modelKey: item.model_key ?? '',
    modelName: item.model_name ?? '',
    category: item.category ?? '',
    colorwayCount: item.colorway_count ?? 0,
    sizeProblem: toProblem(item.size_problem),
    country: item.country ?? 'WW',
    status: toStatus(item.status),
    bannerDate: item.banner_date ?? '',
    isNew: item.is_new ?? false,
    activeFrom: item.banner_active_from || null,
    unitsAfter: item.units_after ?? null,
    rateBeforePct,
    rateAfterPct,
    totalChangePct:
      rateBeforePct !== null && rateAfterPct !== null && rateBeforePct > 0
        ? ((rateAfterPct - rateBeforePct) / rateBeforePct) * 100
        : null,
    savedEurToDate: item.saved_eur_to_date ?? null,
    classification: item.classification ?? '',
    catalogRetired: item.catalog_retired ?? false,
    progress: {
      units: item.progress_units ?? 0,
      days: item.progress_days ?? 0,
      unitsTarget: item.progress_units_target ?? 20,
      daysTarget: item.progress_days_target ?? 30
    },
    imageUrl: item.image_url ?? ''
  }
}

function addSavings(a: number | null, b: number | null): number | null {
  if (a === null) return b
  if (b === null) return a
  return a + b
}

export function toSizingModels(
  data: SizesuggestionspbListSizingModelsResponse
): SizingModels {
  // The API returns one row per (model, country, direction) and the detail
  // endpoint resolves a key to the widest row: the list must show that one.
  const byModel = new Map<string, SizingModelListItem>()
  for (const row of data.models ?? []) {
    const model = toModelListItem(row)
    const kept = byModel.get(model.modelKey)
    if (!kept) {
      byModel.set(model.modelKey, model)
      continue
    }
    const widest =
      (model.unitsAfter ?? 0) > (kept.unitsAfter ?? 0) ? model : kept
    byModel.set(model.modelKey, {
      ...widest,
      // "new" is a property of the model, not of the widest row: the row that
      // carries the activation can be a thinner one.
      isNew: model.isNew || kept.isNew,
      // Rates cannot be summed across rows, but euros can: every row saves its
      // own, so the model's savings are the sum of all of them.
      savedEurToDate: addSavings(kept.savedEurToDate, model.savedEurToDate)
    })
  }
  return {
    cycleDate: data.cycle_date ?? '',
    models: [...byModel.values()].sort(
      (a, b) =>
        statusRank(a) - statusRank(b) ||
        (b.savedEurToDate ?? 0) - (a.savedEurToDate ?? 0) ||
        a.modelKey.localeCompare(b.modelKey)
    )
  }
}

// Lifecycle order: a merchant reads the list top-down as "what is happening now".
const STATUS_RANK: Record<string, number> = {
  WORKING: 1,
  COLLECTING_DATA: 2,
  NO_CLEAR_EFFECT: 3,
  REDIAGNOSIS: 4,
  RETIRED: 5
}

function statusRank(model: SizingModelListItem): number {
  if (model.isNew) return 0
  return STATUS_RANK[model.status] ?? 6
}

export function toSizingModelDetail(
  data: SizesuggestionspbGetSizingModelResponse
): SizingModelDetail {
  const target = direction(
    data.target_direction?.ret_before,
    data.target_direction?.ret_after,
    data.units_before,
    data.units_after,
    data.target_direction?.rate_before
  )
  const opposite = direction(
    data.opposite_direction?.ret_before,
    data.opposite_direction?.ret_after,
    data.units_before,
    data.units_after,
    data.opposite_direction?.rate_before
  )
  const totalBefore =
    data.target_direction?.ret_before !== undefined &&
    data.opposite_direction?.ret_before !== undefined
      ? (data.target_direction.ret_before ?? 0) +
        (data.opposite_direction.ret_before ?? 0)
      : undefined
  const totalAfter =
    data.target_direction?.ret_after !== undefined &&
    data.opposite_direction?.ret_after !== undefined
      ? (data.target_direction.ret_after ?? 0) +
        (data.opposite_direction.ret_after ?? 0)
      : undefined
  const totalRateBefore =
    data.target_direction?.rate_before != null &&
    data.opposite_direction?.rate_before != null
      ? data.target_direction.rate_before + data.opposite_direction.rate_before
      : undefined
  return {
    modelKey: data.model_key ?? '',
    modelName: data.model_name ?? '',
    category: data.category ?? '',
    sizeProblem: toProblem(data.size_problem),
    country: data.country ?? 'WW',
    status: toStatus(data.status),
    bannerDate: data.banner_date ?? '',
    activeFrom: data.banner_active_from || null,
    cycleDate: data.cycle_date ?? '',
    unitsAfter: data.units_after ?? null,
    target,
    opposite,
    total: direction(
      totalBefore,
      totalAfter,
      data.units_before,
      data.units_after,
      totalRateBefore
    ),
    savedEurToDate: data.saved_eur_to_date ?? null,
    colorways: (data.colorways ?? []).map((colorway) => ({
      productId: colorway.product_id ?? '',
      name: colorway.name ?? '',
      units: colorway.units ?? 0,
      imageUrl: colorway.image_url ?? ''
    })),
    quotes: data.quotes ?? [],
    progress: {
      units: data.progress_units ?? 0,
      days: data.progress_days ?? 0,
      unitsTarget: data.progress_units_target ?? 20,
      daysTarget: data.progress_days_target ?? 30
    }
  }
}

function toEvidence(evidenceJson?: string): SizingEventEvidence {
  if (!evidenceJson) return {}
  try {
    const raw = JSON.parse(evidenceJson) as Record<string, unknown>
    return {
      excessPct:
        typeof raw.excess === 'number' ? raw.excess * 100 : undefined,
      units12m: typeof raw.units_12m === 'number' ? raw.units_12m : undefined,
      commentCount:
        typeof raw.comment_count === 'number' ? raw.comment_count : undefined,
      category: typeof raw.category === 'string' ? raw.category : undefined,
      colorwayCount:
        typeof raw.colorway_count === 'number' ? raw.colorway_count : undefined,
      catalogRetired: raw.catalog_retired === true
    }
  } catch {
    return {}
  }
}

function toEvent(event: SizesuggestionspbSizingCycleEventView): SizingCycleEvent {
  const known: SizingEventType[] = [
    'ACTIVATED',
    'RETIRED',
    'REDIAGNOSIS',
    'WATCH'
  ]
  return {
    modelKey: event.model_key ?? '',
    modelName: event.model_name ?? '',
    eventType: known.find((t) => t === event.event_type) ?? 'WATCH',
    sizeProblem:
      event.size_problem === 'TOO_SMALL' || event.size_problem === 'TOO_BIG'
        ? event.size_problem
        : '',
    countries: event.countries ?? [],
    evidence: toEvidence(event.evidence_json)
  }
}

function toModelResult(
  row: SizesuggestionspbSizingModelResult
): SizingModelResult {
  return {
    modelKey: row.model_key ?? '',
    country: row.country ?? 'WW',
    sizeProblem: toProblem(row.size_problem),
    eligible: row.eligible ?? false,
    classification: row.classification ?? 'INELIGIBLE',
    rateBeforeTotal:
      row.rate_t_before != null && row.rate_o_before != null
        ? row.rate_t_before + row.rate_o_before
        : null,
    unitsBefore: row.units_before ?? null,
    unitsAfter: row.units_after ?? null,
    retTargetBefore: row.ret_t_before ?? null,
    retTargetAfter: row.ret_t_after ?? null,
    retOppositeBefore: row.ret_o_before ?? null,
    retOppositeAfter: row.ret_o_after ?? null
  }
}

export function toSizingCycle(
  data: SizesuggestionspbGetSizingCycleResponse
): SizingCycle {
  // Same collapse as toSizingModels: rows come per (model, country, direction)
  // and summing them counts the same units several times.
  const byModel = new Map<string, SizingModelResult>()
  for (const row of data.results ?? []) {
    const result = toModelResult(row)
    const kept = byModel.get(result.modelKey)
    if (!kept || (result.unitsAfter ?? 0) > (kept.unitsAfter ?? 0)) {
      byModel.set(result.modelKey, result)
    }
  }
  return {
    cycleDate: data.cycle_date ?? '',
    actuated: data.actuated ?? false,
    events: (data.events ?? []).map(toEvent),
    results: [...byModel.values()]
  }
}


